// 📖 The Memory Wall's labels + the "Older memories" book (09-24). Pure
// helpers for HouseMemoryWall + HouseMemoryArchive; the data comes from the
// backend's houseMemoryArchive.js (houseMemoryWall / houseMemoryArchive ops).
//
// ⭐ Day keys are HOUSE days ('YYYY-MM-DD', America/Phoenix). The wall used to
// compare them to a UTC "today", so from 5pm house time every label was a day
// off ("yesterday" on today's memories). The server now sends house_today;
// houseTodayKey() is the fallback for an older backend. Day keys are calendar
// dates, never instants: every formatter below reads them at UTC noon.

export const HOUSE_TZ_FALLBACK = 'America/Phoenix';

export function houseTodayKey(tz = HOUSE_TZ_FALLBACK, now = Date.now()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date(now));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch (e) {
    return new Date(now).toISOString().slice(0, 10);
  }
}

const noon = (key) => new Date(`${key}T12:00:00Z`);
const valid = (key) => /^\d{4}-\d{2}-\d{2}$/.test(String(key || '')) && !Number.isNaN(noon(key).getTime());

export function keyPlusDays(key, n) {
  return new Date(noon(key).getTime() + n * 86400000).toISOString().slice(0, 10);
}

/** 'today' · 'yesterday' · 'Sep 21' on the house clock. */
export function wallDayLabel(day, today) {
  if (!valid(day)) return null;
  const t = valid(today) ? today : houseTodayKey();
  if (day === t) return 'today';
  if (day === keyPlusDays(t, -1)) return 'yesterday';
  return shortDay(day);
}

export const shortDay = (key) => noon(key).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
export const longDay = (key) => noon(key).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).replace(',', '');
export const monthName = (m) => noon(`${m}-15`).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

/** A torn-off calendar leaf, drawn with the real day (the 📅 emoji says JUL 17 on a Mac). */
export function leafOf(key) {
  const d = noon(key);
  return {
    mo: d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
    dn: d.getUTCDate(),
    wd: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }).toUpperCase(),
  };
}

// Same prefix the backend writes (houseMemory._deterministicFacts).
export const EVENT_PREFIX = /^House event(?: ended)?(?: \([^)]+\))?:\s*/;
export const displayText = (line) => (line.e ? String(line.t).replace(EVENT_PREFIX, '') : String(line.t));

export function joinNames(list) {
  if (!list || list.length < 2) return (list || []).join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

const hits = (text, q) => !q || String(text).toLowerCase().includes(q.toLowerCase());

/**
 * What one day shows. `q` = the live search (the server matched raw text with
 * the same case-insensitive substring rule) · `reps` = show repeats.
 * → { lines: visible lines, hidden: repeats hidden, who: visitors matching q, count }
 */
export function dayView(day, { q = '', reps = false } = {}) {
  const lines = [];
  let hidden = 0;
  for (const l of day.lines || []) {
    if (!hits(l.t, q)) continue;
    if (l.d && !reps) { hidden += 1; continue; }
    lines.push(l);
  }
  const who = q ? (day.visitors || []).filter(([name]) => hits(name, q)) : [];
  const count = (day.lines || []).filter((l) => reps || !l.d).length;
  return { lines, hidden, who, count };
}

/** Search results: days with something visible, and how many memories match. */
export function searchView(days, q, reps) {
  const shown = [];
  let n = 0;
  for (const d of days || []) {
    const v = dayView(d, { q, reps });
    if (!v.lines.length && !v.who.length) continue;
    n += v.lines.length + v.who.length;
    shown.push(d);
  }
  return { days: shown, n };
}

/** Text split around every case-insensitive hit, for <mark>. */
export function highlightParts(text, q) {
  const s = String(text || '');
  if (!q) return [{ text: s, hit: false }];
  const out = [];
  const lower = s.toLowerCase();
  const needle = q.toLowerCase();
  let at = 0;
  let i = lower.indexOf(needle);
  while (i >= 0 && needle.length) {
    if (i > at) out.push({ text: s.slice(at, i), hit: false });
    out.push({ text: s.slice(i, i + needle.length), hit: true });
    at = i + needle.length;
    i = lower.indexOf(needle, at);
  }
  if (at < s.length) out.push({ text: s.slice(at), hit: false });
  return out;
}

export const repeatCount = (days) => (days || []).reduce((n, d) => n + (d.lines || []).filter((l) => l.d).length, 0);

export const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
